import React from 'react';
import { Layout, Button, Dropdown, Avatar, Space, Typography, Switch, message, theme } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  SwapOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { policyDocumentsAPI } from '../../services/api';
import DocumentAcceptanceModal from '../DocumentAcceptanceModal';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  isMobile?: boolean;
}

const Header: React.FC<HeaderProps> = ({ collapsed, setCollapsed, isMobile }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { token } = theme.useToken();
  const [viewMode, setViewMode] = React.useState<'own' | 'employee'>(() => {
    return (localStorage.getItem('viewMode') as 'own' | 'employee') || 'own';
  });
  const [showAcceptanceModal, setShowAcceptanceModal] = React.useState(false);
  const [pendingMode, setPendingMode] = React.useState<'own' | 'employee'>('own');
  /** Role whose documents must be accepted (may differ from user.role in employee viewMode) */
  const [acceptanceRole, setAcceptanceRole] = React.useState<string>('employee');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleViewModeChange = async (checked: boolean) => {
    const newMode = checked ? 'employee' : 'own';
    
    // Если переключаемся на режим сотрудника, проверяем принятие документов
    if (newMode === 'employee') {
      try {
        const acceptance = await policyDocumentsAPI.checkAcceptance('employee');
        
        if (!acceptance.allAccepted) {
          // Показываем модальное окно для принятия документов сотрудника
          setPendingMode(newMode);
          setAcceptanceRole('employee');
          setShowAcceptanceModal(true);
          return;
        }
      } catch (error) {
        message.error('Ошибка проверки документов');
        console.error('Check acceptance error:', error);
        return;
      }
    }
    
    // Если все в порядке, переключаем режим
    setViewMode(newMode);
    localStorage.setItem('viewMode', newMode);
    
    // При переходе в режим сотрудника - переходим на страницу меню
    if (newMode === 'employee') {
      window.location.href = '/menu';
    } else {
      window.location.reload(); // Для возврата в свой режим - просто перезагрузка
    }
  };

  /** Куда вести после принятия документов (не все роли — «меню сотрудника»). */
  const redirectAfterDocumentAcceptance = () => {
    const u = useAuthStore.getState().user;
    const r = u?.role;
    if (r === 'delivery') {
      window.location.href = '/delivery-dashboard';
      return;
    }
    if (r === 'employee') {
      window.location.href = '/menu';
      return;
    }
    const vm = localStorage.getItem('viewMode') || 'own';
    if (vm === 'employee' && r && ['customer', 'supplier', 'support'].includes(r)) {
      window.location.href = '/menu';
      return;
    }
    window.location.reload();
  };

  const handleAcceptanceComplete = () => {
    setShowAcceptanceModal(false);
    setViewMode(pendingMode);
    localStorage.setItem('viewMode', pendingMode);
    message.success('Документы приняты. Переключение режима...');
    setTimeout(() => redirectAfterDocumentAcceptance(), 500);
  };

  const handleAcceptanceCancel = () => {
    setShowAcceptanceModal(false);
    // Не меняем режим, если пользователь отказался принимать документы
  };

  // Проверяем принятие документов при загрузке (с учётом viewMode=employee)
  React.useEffect(() => {
    const checkDocuments = async () => {
      if (!user) return;

      const vm = localStorage.getItem('viewMode') || 'own';
      const canSwitch = ['customer', 'supplier', 'support'].includes(user.role);
      const effectiveRole =
        vm === 'employee' && canSwitch ? 'employee' : user.role;

      try {
        const acceptance = await policyDocumentsAPI.checkAcceptance(effectiveRole);
        if (!acceptance.allAccepted) {
          setAcceptanceRole(effectiveRole);
          setPendingMode(vm === 'employee' && canSwitch ? 'employee' : 'own');
          setShowAcceptanceModal(true);
        }
      } catch (error) {
        console.error('Error checking document acceptance:', error);
      }
    };

    checkDocuments();
  }, [user]);

  // Показываем переключатель только для customer, supplier и support
  const canSwitchView = user?.role && ['customer', 'supplier', 'support'].includes(user.role);

  const getRoleText = (role: string | undefined) => {
    const roles: { [key: string]: string } = {
      admin: 'Администратор',
      customer: 'Заказчик',
      customer_manager: 'Контролёр заказчика',
      supplier: 'Поставщик',
      employee: 'Сотрудник',
      support: 'Поддержка',
      delivery: 'Доставщик'
    };
    return roles[role || ''] || 'Пользователь';
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile')
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Настройки',
      onClick: () => navigate('/settings')
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      onClick: handleLogout,
      danger: true
    }
  ];

  return (
    <AntHeader
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: token.colorBgContainer,
        padding: isMobile ? '0 12px' : '0 24px',
        boxShadow: '0 6px 20px rgba(16,24,40,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <Button
        type="text"
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={() => setCollapsed(!collapsed)}
        style={{
          fontSize: '16px',
          width: isMobile ? 48 : 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: token.borderRadiusLG,
        }}
      />

      <Space
        size={isMobile ? 'small' : 'middle'}
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          justifyContent: 'flex-end',
          flex: 1,
        }}
      >
        {user ? (
          <>
            {canSwitchView && (
              <Space size="small" style={{ 
                padding: isMobile ? '4px 8px' : '4px 12px', 
                background: token.colorFillTertiary, 
                borderRadius: token.borderRadiusLG,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}>
                <SwapOutlined style={{ color: token.colorPrimary }} />
                {!isMobile ? <Text style={{ fontSize: 13 }}>Режим сотрудника</Text> : null}
                <Switch 
                  checked={viewMode === 'employee'}
                  onChange={handleViewModeChange}
                  size="small"
                />
              </Space>
            )}
            {isMobile ? (
              <Text strong ellipsis style={{ fontSize: 13, maxWidth: 100 }}>
                {user.fullName || user.username}
              </Text>
            ) : (
              <>
                <Text strong style={{ fontSize: 14 }}>
                  {user.username}
                </Text>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({viewMode === 'employee' && canSwitchView ? 'Сотрудник' : getRoleText(user.role)})
                </Text>
              </>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar
                icon={<UserOutlined />}
                style={{ cursor: 'pointer', backgroundColor: token.colorPrimary }}
              />
            </Dropdown>
          </>
        ) : (
          <Button type="primary" icon={<LoginOutlined />} onClick={() => navigate('/login')}>
            {isMobile ? 'Войти' : 'Войти в систему'}
          </Button>
        )}
      </Space>

      <DocumentAcceptanceModal
        visible={showAcceptanceModal}
        role={acceptanceRole || user?.role || 'employee'}
        onAcceptAll={handleAcceptanceComplete}
        onCancel={handleAcceptanceCancel}
      />
    </AntHeader>
  );
};

export default Header;

